import { useEffect, useRef, useState } from 'react'
import type { EiaClient } from '../../api/client'

interface ReportOverlayProps {
  client: EiaClient
  engagementId: string
  onClose: () => void
}

/**
 * THE VIEWER'S ONE ADDITION TO THE STORED DOCUMENT: ONE CENTRED COLUMN, ON SCREEN. B-226, B-227.
 *
 * <p>Every element of the report — paragraphs, headings, rules, tables — shares one column 174mm
 * wide, the text width of the printed A4 page, centred in the frame. Reports rendered since B-227
 * carry that rule themselves and this changes nothing for them; reports stored earlier cap the body
 * at 46rem and paragraphs at 66ch, and this evens them out. A sandboxed cross-document frame cannot
 * be styled from this page, so the rule travels inside `srcdoc`: one `<style>` element, inserted
 * after the document's own and scoped to `screen`, so printing keeps the report's print layout.
 * Nothing is re-rendered and no content changes (`AC-RPT-7`).</p>
 */
export const VIEWER_STYLE =
  '<style data-eia-viewer>@media screen{body{max-width:174mm;margin:24px auto;padding:0 32px}'
  + 'p,ul,ol,blockquote{max-width:none}}'
  // A NARROW FRAME KEEPS THE DOCUMENT'S OWN 16px. Its widest tables are wider than a phone with or
  // without this style; the extra padding would only add to that.
  + '@media screen and (max-width:600px){body{padding:0 16px}}</style>'

export function viewerDocument(stored: string): string {
  const head = stored.indexOf('</head>')
  if (head >= 0) return stored.slice(0, head) + VIEWER_STYLE + stored.slice(head)
  const body = stored.indexOf('<body')
  if (body >= 0) return stored.slice(0, body) + VIEWER_STYLE + stored.slice(body)
  return stored + VIEWER_STYLE
}

/**
 * The report, IN A SANDBOXED IFRAME, as defence in depth. `04 §6`.
 *
 * <p><b>THE PAGE DISPLAYS THE STORED ARTEFACT AND DOES NOT RE-RENDER IT</b> (`AC-RPT-7`). One
 * renderer exists and it is on the server: a report rendered twice by two renderers is two
 * documents that can disagree, and the one the reader saw is not the one that was signed. `marked`
 * and `DOMPurify` are for streaming conversation tokens and are not used here.</p>
 *
 * <p>The sandbox carries NO `allow-same-origin` and NO `allow-scripts`. `report.html` requests
 * nothing over the network (`AC-RPT-3`) — no external font, stylesheet, script or image — so it has
 * nothing legitimate to lose, and a report that somehow did would be unable to reach anything.</p>
 */
export function ReportOverlay(
  { client, engagementId, onClose }: ReportOverlayProps,
): React.JSX.Element {
  const dialog = useRef<HTMLDivElement | null>(null)
  const returnTo = useRef<Element | null>(null)
  const [html, setHtml] = useState<string | null>(null)
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // THE DOCUMENT ITSELF, FETCHED WITH THE TOKEN, and rendered with `srcdoc`.
  //
  // <p>Pointing the frame at the brokered storage URL does not work and cannot be made to: this
  // page sets `frame-src 'self'`, so the browser refuses to frame the storage account and the
  // overlay renders a 0x0 box — "Open report" doing nothing (B-167). A fetch is governed by
  // `connect-src`, which allows `'self'`, and unlike an iframe it can carry a bearer. `srcdoc` is
  // not a URL load, so `frame-src` does not gate it.</p>
  useEffect(() => {
    let live = true
    void client.reportText(engagementId, 'report.html')
      .then((text) => { if (live) setHtml(text) })
      .catch(() => {
        if (live) setError('The report could not be opened. It is still stored; close and retry.')
      })

    // AND THE BROKERED URL SEPARATELY, for "Open in a new tab" — a TOP-LEVEL navigation, which
    // `frame-src` does not govern and which therefore works where the frame does not.
    void client.artefactLink(engagementId, 'report.html')
      .then((link) => { if (live) setUrl(link) })
      .catch(() => { /* the tab is a convenience; the frame is the report */ })

    return () => { live = false }
  }, [client, engagementId])

  // THE STYLESHEET'S OWN SWITCH, which nothing was setting. B-167.
  //
  // `engagement.css` declares `.overlay{display:none}` and turns it on with
  // `body[data-report="open"] .overlay{display:flex}`. The overlay therefore mounted, fetched its
  // document, and stayed invisible — a 0x0 box behind a button that looked dead. `SpecDrawer` has
  // driven `body[data-spec]` this way from the start; this simply follows it.
  useEffect(() => {
    document.body.dataset['report'] = 'open'
    return () => { delete document.body.dataset['report'] }
  }, [])

  useEffect(() => {
    returnTo.current = document.activeElement
    dialog.current?.focus()

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }

    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)

      // FOCUS GOES BACK WHERE IT CAME FROM. A keyboard user who opens the report and closes it
      // should be on the button they pressed, not at the top of the document.
      if (returnTo.current instanceof HTMLElement) returnTo.current.focus()
    }
  }, [onClose])

  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label="Report"
         ref={dialog} tabIndex={-1}>
      {/* `.reader > header`, `.frame`, `.printnote` — the vocabulary `04 §1` names and
          `engagement.css` is written against. The first draft invented `.overlay__bar`,
          `.overlay__frame` and `.overlay__print-note`, which no rule in the stylesheet matches, so
          nothing here had any layout at all. */}
      <div className="reader">
        <header>
          <h2>Report</h2>
          <button type="button" className="btn quiet" onClick={onClose}>Close</button>
          <button type="button" className="btn quiet"
                  onClick={() => { if (url !== null) window.open(url, '_blank') }}
                  disabled={url === null}>
            Open in a new tab
          </button>
        </header>

      {/* `srcdoc`, NOT `src`, AND NEITHER BY PREFERENCE. Pointing at the token-protected route
          answered 401, because an iframe navigation carries no bearer (B-162); pointing at the
          brokered storage URL is refused by this page's own `frame-src 'self'` (B-167). The
          document is fetched instead and handed to the frame directly.

          `sandbox=""` is the empty string deliberately: no scripts, no same-origin, no forms — and
          the document is rendered server-side with raw HTML disabled besides, so it cannot carry a
          script to begin with. Two reasons, because one of them being wrong should not be enough. */}
        {html === null
          ? <p className="frame note">{error ?? 'Preparing the report…'}</p>
          : <iframe className="frame" title="Report" srcDoc={viewerDocument(html)} sandbox=""
                    style={{ border: 0, width: '100%' }} />}

        {/* AN HONEST LIMITATION, STATED ON THE PAGE. "The document controls where pages break;
            the browser controls the margin furniture." */}
        <p className="printnote">
          Printing: the document sets its own page breaks. Page numbers and running headers come
          from your browser's print dialogue, not from the report.
        </p>
      </div>
    </div>
  )
}
